import { z } from "zod";
import { ModuleDefinitionFieldSchema, isBoundField, } from "../form-bindings/index.js";
// A reusable group of form fields in the session-note form builder (ADR-032).
// A module is named, optionally described, and bound to one entity type, which
// says what its values hang off — the case, the session, or the participants of
// either. The binding is stored and shown; the referential-integrity rules it
// will drive are developer-owned and not yet built.
//
// `entityLink` is the key of a row in the entity-link lookup; `entityLinkLabel`
// is that row's wording, resolved here so a list does not have to join it back.
// `archived` travels with every module because a template keeps one archived
// after it was placed, and has to be able to say so.
export const FormModuleSchema = z.object({
    id: z.guid(),
    name: z.string(),
    description: z.string(),
    entityLink: z.string(),
    entityLinkLabel: z.string(),
    archived: z.boolean(),
    // The version templates use when they place the module: its latest
    // published one, or null for a module not yet published, which templates
    // cannot place (ADR-040).
    publishedVersion: z.int().positive().nullable(),
    // What publishing the saved working copy would do (ADR-040): "fields" mints
    // a version, and new versions of the templates placing it; "wording" updates
    // the latest version's name and description in place; null means nothing is
    // waiting. Always "fields" for a module never published. The server's to
    // decide: a client shows it, never works it out.
    unpublishedChange: z.enum(["fields", "wording"]).nullable(),
});
export const FormModulesResponseSchema = z.object({
    formModules: FormModuleSchema.array(),
});
// The entity types a module can bind to, as offered by the "Type" picker. Held
// as reference data rather than a code enum (ADR-032): the field dictionary will
// key off these, and the set is expected to grow beyond the first six. What each
// means for a module's answers is code, in ENTITY_LINKS, which lists every one.
export const FormModuleEntityLinkSchema = z.object({
    key: z.string(),
    label: z.string(),
});
export const FormModuleEntityLinksResponseSchema = z.object({
    entityLinks: FormModuleEntityLinkSchema.array(),
});
// The write body for POST /api/admin/form-modules. Trimmed here rather than in
// the handler: the name's uniqueness index is on lower(name), so surrounding
// whitespace has to be gone before the database decides whether it is a
// duplicate. The id and the resolved label are the server's to supply.
export const CreateFormModuleSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2000),
    entityLink: z.string().min(1),
});
// Answers "is this name free?" while the new-module form is being filled in, so
// a name already in use is caught as it is typed rather than on save. The unique
// index is still what enforces it — two administrators can pass this check at
// the same moment and only one can then save.
export const FormModuleNameAvailabilitySchema = z.object({
    available: z.boolean(),
});
// What a module holds, as one document. An object rather than a bare array so
// the definition can carry more than its fields later without a migration — the
// same reason a collection on the wire is enveloped.
export const ModuleDefinitionSchema = z.object({
    // Values key on a field's id, so two fields sharing one would share an
    // answer. The builder mints a fresh id per field; this holds the line for
    // anything else that writes a definition. A binding shows one value, so a
    // module holds it once; the repeat is named where it stands.
    fields: ModuleDefinitionFieldSchema.array()
        .refine((fields) => new Set(fields.map((field) => field.id)).size === fields.length, { message: "Each field needs an id of its own" })
        .superRefine((fields, ctx) => {
        const bound = new Set();
        fields.forEach((field, index) => {
            if (!isBoundField(field))
                return;
            if (bound.has(field.binding)) {
                ctx.addIssue({
                    code: "custom",
                    path: [index, "binding"],
                    message: "This field is already on the module",
                });
            }
            bound.add(field.binding);
        });
    }),
});
// One published version of a module: the wording and fields it was published
// with (ADR-040). What a session template's preview shows of a module it holds,
// read at the version the template pins.
export const FormModuleVersionSchema = z.object({
    version: z.int().positive(),
    name: z.string(),
    description: z.string(),
    definition: ModuleDefinitionSchema,
});
// A module as session templates hold it: one published version, with that
// version's wording — never the working copy, which nothing but the module's
// own screens shows (ADR-040). A template's own modules are at the version it
// pins; the library's are at the latest, the one a template adds. `archived`
// travels because a template keeps an archived module it already holds, and
// says so.
export const TemplateModuleSchema = z.object({
    id: z.guid(),
    version: z.int().positive(),
    name: z.string(),
    description: z.string(),
    entityLink: z.string(),
    entityLinkLabel: z.string(),
    archived: z.boolean(),
});
// GET /api/admin/form-modules/published — the modules a template can add: every
// one not archived that has published, at its latest version. A module never
// published is not in it: there is nothing yet for a template to hold.
export const PublishedFormModulesResponseSchema = z.object({
    formModules: TemplateModuleSchema.array(),
});
// A module with the fields it holds. The list deliberately does not carry these
// — a definition per row is weight the list has no use for — so the detail
// shape is its own schema rather than a wider FormModuleSchema.
export const FormModuleDetailSchema = FormModuleSchema.extend({
    // The working copy the canvas edits, with the base's name and description
    // (ADR-040): saved, but seen by nothing else until it is published.
    definition: ModuleDefinitionSchema,
    // Counts the working copy's saves from 1, so a save or publish can say which
    // one it was made from — as a session note's revision does.
    revision: z.int().positive(),
});
// The write body for PUT /api/admin/form-modules/:id: the working copy whole.
// What is absent is absent, not unchanged. `entityLink` is carried so the body
// is whole, and refused by the service if it differs from the stored one —
// what a module is about cannot change once it has fields.
export const UpdateFormModuleSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2000),
    entityLink: z.string().min(1),
    definition: ModuleDefinitionSchema,
    // Not part of the module as it is to be: a check on it. The revision the
    // save was edited from; one that is no longer current is refused,
    // FORM_MODULE_CHANGED, so a save cannot put back a change it never saw.
    revision: z.int().positive(),
});
// The body of POST /api/admin/form-modules/:id/publish: the revision the
// administrator saw when they chose to publish. One that is no longer current
// is refused, FORM_MODULE_CHANGED, so a publish never carries changes unseen.
export const PublishFormModuleSchema = z.object({
    revision: z.int().positive(),
});
// The body of POST /api/admin/form-modules/:id/discard: the revision the
// administrator saw when they chose to discard their saved changes. One that is
// no longer current is refused, FORM_MODULE_CHANGED, so a discard never throws
// away a change unseen.
export const DiscardFormModuleSchema = z.object({
    revision: z.int().positive(),
});
// GET /api/admin/form-modules/:id/templates — the templates still in use that
// hold the module, each at its current version: the reach a publish will have
// (ADR-032 J), shown before it is taken.
export const FormModuleTemplatesResponseSchema = z.object({
    sessionTemplates: z
        .object({ id: z.guid(), name: z.string(), version: z.int().positive() })
        .array(),
});
// What a client is told about a module write beyond its status, as an error's
// code (ADR-033 rule 7). All are 409s, told apart by these: one sends the
// administrator back to reload, one to pick another name, and one says a
// module never published has no version to go back to.
export const FormModuleErrorCodeSchema = z.enum([
    "FORM_MODULE_CHANGED",
    "FORM_MODULE_NAME_TAKEN",
    "FORM_MODULE_NEVER_PUBLISHED",
]);
export const FormModuleErrorCode = FormModuleErrorCodeSchema.enum;
